<template>
  <question-base-wrapper v-bind="baseWrapperProps">
    <validation-provider
      v-slot="{ errors, failedRules }"
      :data-unit-test="`${stepId}_${questionId}`"
      :vid="`${stepId}_${questionId}`"
      :name="questionId"
      :rules="validationRules ? validationRules.search : 'required'"
    >
      <input
        type="hidden"
        :value="questionData[questionFields.search]"
      >

      <qz-autocomplete
        v-model="searchModel"
        :data-test="fieldTrackerId.list"
        :placeholder="helpers.getPlaceholderResponsiveText(searchPlaceholder, isMobile)"
        :items="searchItems"
        :is-options-capitalize="optionsConfig.isCapitalize"
        :no-option-message="otherOption ? 'No matching option found, please select “not listed” below' : undefined"
        :errors="getFieldErrors('search', failedRules, errors)"
        :update-search="onSearch"
      />

      <checkbox-square
        v-if="otherOption"
        v-model="isOtherCheckedModel"
        class="mt-4"
        :name="`${stepId}.${questionId}`"
        :data-test="`${snakeCase(questionFields.other)}_checkbox`"
        :title="otherOption.text"
      />
    </validation-provider>
    <template #subsection="{observer}">
      <form-row
        v-if="isOtherCheckedModel"
        :label="otherInputTitle"
        :question="otherSubtitle"
        :error="formObserver.validated && observer.failed"
      >
        <template
          v-if="otherHelpText"
          #helpText
        >
          <div v-html="otherHelpText.value || otherHelpText" />
        </template>
        <validation-provider
          v-slot="{ errors, failedRules }"
          :rules="validationRules ? validationRules.other : 'required'"
          :name="questionId"
          :vid="`${stepId}_${questionId}`"
        >
          <text-input
            v-model="questionData[questionFields.other]"
            :placeholder="otherInputPlaceholder"
            :data-test="fieldTrackerId.other"
            :max-length="otherInputMaxLength"
            :error-messages="getFieldErrors('other', failedRules, errors)"
          />
        </validation-provider>
      </form-row>
    </template>
  </question-base-wrapper>
</template>

<script>
import QuestionBase from './Base';
import QuestionBaseWrapper from './BaseWrapper';
import TextInput from '@/components/TextInput/TextInput';
import { capitalize, startCase } from 'lodash';
import createOptions from '@/utils/createOptions';

export default {
  name: 'question-base-search-other',
  extends: QuestionBase,
  components: {
    QuestionBaseWrapper,
    TextInput,
  },
  data () {
    return {
      fields: ['search'],
      searchItems: [],
      searchPayload: null,
    };
  },
  props: {
    otherInputTitle: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    otherSubtitle: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    otherInputPlaceholder: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    otherHelpText: {
      default: null,
      validator: value => (typeof value === 'string') || value === false,
    },
    searchType: {
      type: String,
      default: '',
    },
    otherInputMaxLength: {
      type: Number,
      default: null,
    },
    topGroup: {
      type: String,
      default: '',
    },
    isImmediate: {
      type: Boolean,
      default: false,
    },
    responseModel: {
      type: Object,
      default: () => ({
        value: 'value',
        name: 'name',
      }),
    },
    payload: {
      type: [Object, Array],
      default: null,
    },
  },
  computed: {
    searchPlaceholder () {
      const { changedPlaceholder, placeholder } = this.optionsConfig;

      if (this.isOtherCheckedModel) {
        return changedPlaceholder;
      }

      return placeholder;
    },
    otherOption () {
      return this.optionsConfig.other;
    },
    isCustomFieldOtherConfigured () {
      return !!this.customFields.other;
    },
    searchModel: {
      get () {
        if (this.optionsConfig.isTextAsValue && !this.isOtherCheckedModel) {
          const item = this.getSearchedItem();
          return item?.value;
        }
        return this.questionData[this.questionFields.search];
      },
      set (value) {
        if (!value?.text && !value?.value && this.isOtherCheckedModel) return;

        // To keep consistency with live products used 'text' of item as property value
        this.questionData[this.questionFields.search] = this.optionsConfig.isTextAsValue ? value?.text : value?.value;

        // Reset other field when it was set up from config file
        if (this.isCustomFieldOtherConfigured) this.questionData[this.questionFields.other] = null;
      },
    },
    isOtherCheckedModel: {
      get () {
        return (this.questionData[this.questionFields.search] && this.questionData[this.questionFields.search] === this.otherOption?.value) || !!this.questionData[this.questionFields.other];
      },
      set (val) {
        if (val) {
          // Search input and checkbox value set to the same key in questionData
          this.questionData[this.questionFields.search] = this.otherOption?.value;

          return;
        }
        // Reset fields when isOtherCheckedModel unchecked
        // Clear 'search' only when selected value from otherOption
        this.questionData[this.questionFields.search] = null;

        if (this.isCustomFieldOtherConfigured) this.questionData[this.questionFields.other] = null;
      },
    },
    fieldTrackerId () {
      const trackerId = `${this.stepId}_${this.questionId}`;
      return {
        list: `${trackerId}_list`,
        other: `${trackerId}_other`,
      };
    },
  },
  async created () {
    if (this.isCustomFieldOtherConfigured) {
      this.fields.push('other');
    }

    if (this.optionsConfig?.options) {
      const isArray = Array.isArray(this.optionsConfig.options);
      if (isArray && this.optionsConfig.options.some(item => Object.prototype.hasOwnProperty.call(item, 'header'))) {
        this.searchItems = createOptions({
          source: this.optionsConfig.options,
        });
      } else {
        this.searchItems = this.helpers.createOptions({
          source: this.optionsConfig.options,
        });
      }

      this.searchModel = this.getSearchedItem();
      return;
    }

    if (this.payload) {
      this.initSearchPayload();
    }
    if (this.stepData[this.questionFields.search] || this.isImmediate) {
      await this.onSearch('???');

      if (this.isOtherCheckedModel) return;

      this.searchModel = this.getSearchedItem();
    }
  },
  methods: {
    async onSearch (value) {
      if (this.optionsConfig?.options) return;

      try {
        if (value === null) return;

        const response = await this.$api[this.searchType]({ ...this.searchPayload, query: value });

        if (response.status === 'success') {
          if (Array.isArray(response.data)) {
            const { value, name } = this.responseModel;

            this.searchItems = response.data.map(item => ({
              value: +item[value] || item[value],
              text: this.optionsConfig.isOptionTextFormatted
                ? this.formatOptionText(item[name])
                : item[name],
            }));
          } else {
            this.setGroupedOptions(response.data);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    formatOptionText (value) {
      return value
        .toLowerCase()
        .replace(/\w+/g, capitalize)
        .replace('Pco', 'PCO')
        .replace(' Ua', ' UA');
    },
    getSearchedItem () {
      return this.searchItems.find((item) => {
        // To keep consistency with live products - getting item by 'text'
        if (this.optionsConfig.isTextAsValue) {
          return this.questionData[this.questionFields.search] === item.text;
        }

        return this.questionData[this.questionFields.search] === item.value;
      });
    },
    setGroupedOptions (options) {
      let groups = Object.keys(options);

      if (this.topGroup) {
        groups = Object.keys(options).filter(item => item.localeCompare(this.topGroup));

        this.searchItems.push({ header: startCase(this.topGroup) });
        this.searchItems.push(...this.helpers.createOptions({ source: options[this.topGroup] }));
      }

      groups.sort().forEach(group => {
        this.searchItems.push({ header: startCase(group) });
        this.searchItems.push(...this.helpers.createOptions({ source: options[group] }));
      });
    },
    async initSearchPayload () {
      Object.keys(this.payload).forEach((item) => {
        // init dynamic payload and track related fields by '@' and watch
        // if value change call search endpoint
        if (typeof this.payload[item] === 'string' && this.payload[item].charAt(0) === '@') {
          this.$watch(`stepData.${[this.payload[item].slice(1)]}`, async (val) => {
            this.searchItems = [];

            if (!val) return;

            this.searchPayload = { ...this.searchPayload, [item]: val };
            await this.onSearch();
          }, {
            immediate: true,
          });

          return;
        }

        // init static payload
        this.searchPayload = { ...this.searchPayload, [item]: this.payload[item] };
      });
    },
  },
};
</script>
